/**
 * The `/calendar` URL contract (FE-REQUIREMENT §4.1). Every value is untrusted input (user-editable, deep-linkable), so each
 * is narrowed to a closed shape here and an unrecognised value degrades to "absent" (= the default), never throws.
 *
 *   week     integer offset from the server-anchored week; 0 (the default) is OMITTED so the router keeps the URL clean.
 *   view     `week` | `list`; absent = the default (`list` at a compact viewport, else `week`).
 *   clients  client tenant ids. ⛔ An EMPTY list is ABSENT, never an empty selection: absent means "all managed clients",
 *            which the data hook expands to explicit ids (ADR-017). Passing `[]` through would be the way a home-tenant
 *            result gets presented as the portfolio.
 *   country  a place id. Accepted so a deep link can carry it, but NEVER sent to the server (no resolver reads it); the page
 *            says so instead (AC-4q).
 */
export interface CalendarSearch {
  week?: number
  view?: 'week' | 'list'
  clients?: string[]
  country?: string
}

const MAX_WEEK = 520 // ten years either side is already absurd; beyond it is a mangled URL, not a request

export function parseCalendarSearch(search: Record<string, unknown>): CalendarSearch {
  const rawWeek = typeof search.week === 'string' && search.week.trim() !== '' ? Number(search.week) : search.week
  const week = typeof rawWeek === 'number' && Number.isInteger(rawWeek) && rawWeek !== 0 && Math.abs(rawWeek) <= MAX_WEEK ? rawWeek : undefined

  const view = search.view === 'list' ? 'list' : search.view === 'week' ? 'week' : undefined

  // The router JSON-decodes search values, so `clients` may arrive as an array, or (a lone uuid does not parse as JSON)
  // as a plain string.
  const rawClients = Array.isArray(search.clients) ? search.clients : typeof search.clients === 'string' ? [search.clients] : []
  const clients = [...new Set(rawClients.filter((c): c is string => typeof c === 'string' && c.trim() !== ''))]

  const country = typeof search.country === 'string' && search.country.trim() !== '' ? search.country : undefined

  return { week, view, clients: clients.length > 0 ? clients : undefined, country }
}

/**
 * A control changes ONLY the param it owns (AC-4p): merge a one-key patch into the CURRENT search, leaving every other param
 * exactly as it was. A patch value of `undefined` REMOVES that param (the router omits it), so "This week" and "clear clients"
 * leave no `week=0` / `clients=[]` noise behind.
 */
export function mergeCalendarSearch(prev: CalendarSearch, patch: Partial<CalendarSearch>): CalendarSearch {
  return { ...prev, ...patch }
}
